// Google Tag Manager utility functions

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Push data to Google Tag Manager dataLayer
 * @param data - Data to push to dataLayer
 */
export const gtmPush = (data: Record<string, any>) => {
  // Only track in production
  if (import.meta.env.PROD && typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

/**
 * Track a custom event in GTM
 * @param eventName - Name of the event
 * @param parameters - Event parameters
 */
export const gtmEvent = (eventName: string, parameters?: Record<string, any>) => {
  gtmPush({
    event: eventName,
    ...parameters,
  });
};

/**
 * Track user login event
 * @param method - Login method (e.g., 'wallet', 'email')
 * @param userId - User identifier (blockchain address)
 */
export const gtmLogin = (method: string, userId: string) => {
  gtmEvent('login', {
    method,
    user_id: userId,
  });
};

/**
 * Track user logout event
 */
export const gtmLogout = () => {
  gtmEvent('logout');
};

/**
 * Track wallet connection event
 * @param walletType - Type of wallet (e.g., 'MetaMask', 'WalletConnect')
 */
export const gtmWalletConnect = (walletType: string) => {
  gtmEvent('wallet_connect', {
    wallet_type: walletType,
  });
};

/**
 * Track wallet disconnection event
 */
export const gtmWalletDisconnect = () => {
  gtmEvent('wallet_disconnect');
};

/**
 * Track bet placement event (no sensitive data)
 */
export const gtmBetPlaced = () => {
  gtmEvent('bet_placed');
};

/**
 * Track bet win event (no sensitive data)
 */
export const gtmBetWin = () => {
  gtmEvent('bet_win');
};

/**
 * Track page view
 * @param pageName - Name of the page
 * @param pagePath - Path of the page
 */
export const gtmPageView = (pageName: string, pagePath: string) => {
  gtmEvent('page_view', {
    page_name: pageName,
    page_path: pagePath,
  });
};

/**
 * Track transaction event (for blockchain transactions - no sensitive data)
 * @param transactionType - Type of transaction (withdraw, claim, etc.)
 */
export const gtmTransaction = (transactionType: string) => {
  gtmEvent('transaction', {
    transaction_type: transactionType,
  });
};

/**
 * Set user properties in GTM
 * @param userId - User identifier
 * @param properties - User properties
 */
export const gtmSetUser = (userId: string, properties?: Record<string, any>) => {
  gtmPush({
    user_id: userId,
    ...properties,
  });
};

